import {
  ControlledAutocompleteSelect,
  GenericInput,
  Pagination,
  Table,
  useSortableTable
} from '@hm/fundamental'
import {mdiMagnify} from '@mdi/js'
import {useCustomPillsRender} from '@widgets/report-filters'
import moment from 'moment'
import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {useFiltersContext} from '../../providers/FiltersProvider'

import {
  MaterialInstallationReport,
  MaterialInstallationReportColumn
} from '@refra/modules/refra-flexible-report/types'

const DEFAULT_VISIBLE_COLUMNS: MaterialInstallationReportColumn[] = [
  'plantName',
  'kilnName',
  'interventionDate',
  'feedingDate',
  'fromMeter',
  'toMeter',
  'manufacturerName',
  'refractoryName',
  'refractoryRevision',
  'heatZones',
  'classificationClass',
  'classification',
  'classificationGroup',
  'year'
]

const OPTIONAL_COLUMNS: MaterialInstallationReportColumn[] = [
  'shutdownName',
  'shutdownStatus',
  'updatedOn',
  'isMortarRequired',
  'kilnProcessSeparator',
  'kilnProcessPreheater',
  'kilnProcessCalciner',
  'refractoryIsActive',
  'manufacturerCountryCode'
]

const ALL_COLUMNS = [...DEFAULT_VISIBLE_COLUMNS, ...OPTIONAL_COLUMNS]

const DATE_COLUMNS = new Set<MaterialInstallationReportColumn>([
  'feedingDate',
  'interventionDate',
  'updatedOn'
])

const DATE_FORMAT = 'DD.MM.YYYY'

// ISO dates are kept raw so they sort chronologically
const toSortValue = (
  row: MaterialInstallationReport,
  key: MaterialInstallationReportColumn
): string | number | boolean => {
  const value = row[key]
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.join(', ')
  return value
}

const toLabel = (key: string): string =>
  key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())

export const InstallationsTable = () => {
  const {t} = useTranslation()
  const {report} = useFiltersContext()
  const [textFilter, setTextFilter] = useState('')
  const [paginationConfig, setPaginationConfig] = useState({
    pageNumber: 0,
    pageSize: 20
  })

  const reportData = useMemo(() => report?.materialInstallations ?? [], [report])

  const columnOptions = useMemo(
    () => ALL_COLUMNS.map((key) => ({label: toLabel(key), value: key})),
    []
  )

  const [selectedColumns, setSelectedColumns] =
    useState<(string | number)[]>(DEFAULT_VISIBLE_COLUMNS)

  const [groupBy, setGroupBy] = useState<(string | number)[]>([])

  const columnsCustomPills = useCustomPillsRender(columnOptions.length, () =>
    setSelectedColumns([])
  )
  const groupByCustomPills = useCustomPillsRender(columnOptions.length, () => setGroupBy([]))

  const visibleColumnsDef = useMemo(() => {
    const renderCell = (row: MaterialInstallationReport, key: MaterialInstallationReportColumn) => {
      const value = row[key]
      if (value === null || value === undefined || value === '') return ''
      if (Array.isArray(value)) return value.join(', ')
      if (typeof value === 'boolean') return value ? t('common.yes') : t('common.no')
      if (DATE_COLUMNS.has(key)) return moment(value).format(DATE_FORMAT)
      return String(value)
    }

    return ALL_COLUMNS.filter((key) => selectedColumns.includes(key)).map((key) => ({
      label: toLabel(key),
      value: (row: MaterialInstallationReport) => toSortValue(row, key),
      render: (row: MaterialInstallationReport) => renderCell(row, key)
    }))
  }, [selectedColumns, t])

  const tableColumnsDef =
    visibleColumnsDef.length > 0
      ? visibleColumnsDef
      : [{label: '', value: (row: MaterialInstallationReport) => row.refractoryName}]
  const config = useSortableTable<MaterialInstallationReport>(tableColumnsDef, [selectedColumns, t])

  const filteredByTextReport = useMemo(() => {
    const query = textFilter.trim().toLowerCase()

    if (!query) {
      return reportData
    }

    return reportData.filter((row) =>
      ALL_COLUMNS.some((key) => String(toSortValue(row, key)).toLowerCase().includes(query))
    )
  }, [reportData, textFilter])

  const paginatedReport = useMemo(
    () =>
      filteredByTextReport.slice(
        paginationConfig.pageNumber * paginationConfig.pageSize,
        (paginationConfig.pageNumber + 1) * paginationConfig.pageSize
      ),
    [filteredByTextReport, paginationConfig]
  )

  return (
    <div className="flex flex-col gap-4">
      {reportData.length === 0 ? (
        <p className="py-8 text-center">
          {t('refraFlexibleReport.report.installationsSection.noItemsFound')}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2">
            <GenericInput
              name="refra_report_text_filter"
              defaultValue={textFilter}
              icon={mdiMagnify}
              placeholder={t('refraFlexibleReport.report.installationsSection.filterPlaceholder')}
              onChange={(text) => {
                setTextFilter(text)
                setPaginationConfig((prev) => ({...prev, pageNumber: 0}))
              }}
              data-cy="refra_report_text_filter"
            />
            <ControlledAutocompleteSelect
              label={t('refraFlexibleReport.report.installationsSection.columns')}
              options={columnOptions}
              selectedValues={selectedColumns}
              onChange={setSelectedColumns}
              isMultiSelect
              showPills
              renderPills={selectedColumns.length > 4 ? columnsCustomPills : undefined}
              data-cy="refra_report_columns"
            />
            <ControlledAutocompleteSelect
              label={t('refraFlexibleReport.report.installationsSection.groupBy')}
              options={columnOptions}
              selectedValues={groupBy}
              onChange={setGroupBy}
              isMultiSelect
              showPills
              renderPills={groupBy.length > 4 ? groupByCustomPills : undefined}
              data-cy="refra_report_group_by"
            />
          </div>
          <div className="overflow-x-auto">
            <Table className="whitespace-nowrap" config={config} data={paginatedReport} />
          </div>
          <Pagination
            label={''}
            totalItems={filteredByTextReport.length}
            onChange={(pageNumber, pageSize) => setPaginationConfig({pageNumber, pageSize})}
            defaultPage={paginationConfig.pageNumber}
            defaultPageSize={paginationConfig.pageSize}
          />
        </>
      )}
    </div>
  )
}
